import { faTrashCan } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { ComponentWithPermissions } from '../../../../../baseComponents/CreateRoutering/types'
import TwinIcon from '../../../../../baseComponents/TwinIcon'
import TwinTrans from '../../../../../baseComponents/TwinTrans'
import { CustomSelectSearchBarWithNotInFilteredItems } from '../../../../../forms/CustomSelect'
import FormRenderer from '../../../../../forms/FormRenderer'
import { FormRender } from '../../../../../forms/FormRenderer/types'
import { SubtitleMultipleCbxSelector } from '../../../../../forms/MultipleCheckboxSelector/types'
import TwinForm from '../../../../../forms/TwinForm'
import { twinFetchPostJSON } from '../../../../../utils/globals/data'
import withLoading from '../../../../../utils/hoc/withLoading'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../utils/reducers'
import { Features, FeaturesPayload } from '../../../../../utils/reducers/products/features'
import { parseFeatureValuesToString } from './functions'
import ProductLayout from '../..'
import { ProductExtendedTyShort } from '../../../types'
import { permissionCheck } from '../../../../../baseComponents/PermissionChecker/function'


type FeaturesProductProps = ComponentWithPermissions & FeaturesLogicProductProps & {
    getData: () => Promise<void>
}

const FeaturesProduct: React.FC<FeaturesProductProps> = ({ basicData, getData, userPermissions, features, ...rest }) => {
    const { addSelectedFeature, deleteSelectedFeature, selectedFeatures, parsedFeaturesValues } = useFeaturesLogicProduct({ features, basicData, ...rest })
    const { t } = useTwinTranslation()
    if (!features || !basicData) {
        return null
    }
    const haveUpdatePermission = permissionCheck(userPermissions, 'update')
    return (
        <TwinForm className='flex flex-auto' action='/api/app/product/assignFeatureValuesToProduct' onSubmit={getData} >
            <ProductLayout basicData={basicData} buttonIsDisabled={false} userPermissions={userPermissions}>
                <div className='flex flex-auto h-1 flex-col product_container'>
                    <input type='hidden' value={parsedFeaturesValues} name='FeaturesValuesIds' />
                    <input type='hidden' value={basicData?.id} name='ProductId' />
                    {Object.keys(selectedFeatures).length < Object.keys(features).length && <CustomSelectSearchBarWithNotInFilteredItems className='features_selector ' excludeItems={Object.keys(selectedFeatures)} items={features} label={t('selectFeature', 'Seleccionar característica')} onChange={(id) => addSelectedFeature(id, [])} name='select_features' value='' readOnly={!haveUpdatePermission} />}
                    <RenderFeatures features={features} selectedFeatures={selectedFeatures}  {...{ addSelectedFeature, deleteSelectedFeature, haveUpdatePermission }} />
                </div>
            </ProductLayout>
        </TwinForm >
    )
}

interface FeaturesLogicProductProps extends ReduxFeatures {
    basicData: ProductExtendedTyShort | null
}

export type SelectedFeaturesTy = { [FeatureId: string]: number[] }

const useFeaturesLogicProduct = ({ features, basicData }: FeaturesLogicProductProps) => {
    const id = basicData?.id
    const [selectedFeatures, setSelectedFeatures] = useState<SelectedFeaturesTy>({})

    const getNotSelectedFeatures = useCallback(async () => {
        if (features) {
            const featuresValuesProduct = await twinFetchPostJSON('/api/app/product/getFeatureValuesOfProduct', { ProductId: id })
            setSelectedFeatures(featuresValuesProduct)
        }
    }, [features, id])

    const addSelectedFeature = useCallback((id: string, values: number[]) => {
        setSelectedFeatures((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return { ...copy, [id]: values }
        })
    }, [setSelectedFeatures])

    const deleteSelectedFeature = useCallback((id: string) => {
        setSelectedFeatures((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            delete copy[id]
            return { ...copy }
        })
    }, [setSelectedFeatures])
    const parsedFeaturesValues = useMemo(() => parseFeatureValuesToString(selectedFeatures), [selectedFeatures])

    useEffect(() => {
        getNotSelectedFeatures()
    }, [getNotSelectedFeatures])

    return { addSelectedFeature, deleteSelectedFeature, selectedFeatures, parsedFeaturesValues }
}

interface RenderFeaturesProps {
    features: Features
    selectedFeatures: SelectedFeaturesTy
    deleteSelectedFeature: (id: string) => void
    addSelectedFeature: (id: string, values: number[]) => void
    haveUpdatePermission: boolean
}

const RenderFeatures: React.FC<RenderFeaturesProps> = ({ features, selectedFeatures, addSelectedFeature, deleteSelectedFeature, haveUpdatePermission }) => {
    const { t } = useTwinTranslation()
    const renderThis: JSX.Element[] = []
    if (features) {
        const subtitles: SubtitleMultipleCbxSelector = {
            subtitleOptions: t('allFeatures', 'Todas las características'),
            subtitleSelectedOptions: t('selectedFeatures', 'Características seleccionadas')
        }

        for (const key in selectedFeatures) {
            const fields: FormRender<any> = [
                {
                    cols: 1,
                    elements: [
                        {
                            name: '',
                            component: 'MultipleCheckboxSelector',
                            subtitles: subtitles,
                            items: features[key]?.values || [],
                            defaultValue: selectedFeatures[key],
                            onChange: (values) => addSelectedFeature(key, values),
                            readOnly: !haveUpdatePermission
                        }
                    ]
                }
            ]
            renderThis.push(
                <div className='my-30' key={features[key]?.id}>
                    <div className='flex justify-between items-center'>
                        <span className='medium14 uppercase'>{features[key]?.name}</span>
                        {haveUpdatePermission ?
                            <span className='cursor-pointer hover:text-red-BA' onClick={() => deleteSelectedFeature(key)}>
                                <TwinIcon icon={faTrashCan} className='mr-8' />
                                <TwinTrans transKey='delete'>Eliminar</TwinTrans>
                            </span>
                        :
                            null
                        }
                    </div>
                    <FormRenderer sections={fields} antiUnsaveTrigger={true} />
                </div>
            )
        }
    }
    return (
        <div>
            {renderThis}
        </div>
    )
}

const featuresProductDispatch = {
    setFeatures: (payload: FeaturesPayload) => ({ type: 'CHANGE_FEATURES', payload }),
}

const mapFeaturesConnector = (state: AllReduxPayloads) => ({ features: state.features })
const featuresConnect = connect(mapFeaturesConnector, featuresProductDispatch)
export type ReduxFeatures = ConnectedProps<typeof featuresConnect>

const featuresConnectLoading = withLoading(FeaturesProduct, [{ fetchUrl: '/api/app/product/feature/getAllFeaturesComplete', propName: 'features', setFunctionName: 'setFeatures' }])

const FeaturesProductConnect = featuresConnect(featuresConnectLoading)

export default FeaturesProductConnect